import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mic, Send } from 'lucide-react';

const RustChat = () => {
  const [msg, setMsg] = useState('');
  const [messages, setMessages] = useState<{ question: string; answer: string }[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceMode, setVoiceMode] = useState(true);
  const [searchMode, setSearchMode] = useState(true);
  const recognitionRef = useRef<any>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [dragPos, setDragPos] = useState({ x: window.innerWidth - 380, y: 100 });
  const dragRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll até o final ao adicionar mensagem
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Salva modo voz
  useEffect(() => {
    const saved = localStorage.getItem('rustchat-voiceMode') === 'true';
    setVoiceMode(saved);
  }, []);
  useEffect(() => {
    localStorage.setItem('rustchat-voiceMode', String(voiceMode));
  }, [voiceMode]);

  // Minimizar no modo busca
  useEffect(() => {
    if (location.pathname === '/search') setCollapsed(true);
  }, [location.pathname]);

  // Drag-and-drop
  useEffect(() => {
    let isDragging = false;
    const handleMouseDown = (e: MouseEvent) => {
      if ((e.target as HTMLElement).dataset.drag === 'true') isDragging = true;
    };
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) setDragPos({ x: e.clientX - 180, y: e.clientY - 30 });
    };
    const handleMouseUp = () => (isDragging = false);

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Injeção de estilo da barra de rolagem (Chrome/Edge)
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      #rustchat-container ::-webkit-scrollbar {
        width: 8px;
      }
      #rustchat-container ::-webkit-scrollbar-track {
        background: transparent;
      }
      #rustchat-container ::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: 4px;
        border: 2px solid transparent;
        background-clip: content-box;
      }
      #rustchat-container ::-webkit-scrollbar-thumb:hover {
        background-color: rgba(255, 255, 255, 0.5);
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const speakText = (text: string) => {
    if (!voiceMode || typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') return;
    if (window.speechSynthesis.speaking) window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'pt-BR';
    utterance.onend = () => setIsSpeaking(false);
    setIsSpeaking(true);
    window.speechSynthesis.speak(utterance);
  };

  const perguntar = async () => {
    if (!msg.trim()) return;
    if (msg.length > 300) return alert('Limite de 300 caracteres.');

    const novaPergunta = { question: msg, answer: 'Buscando...' };
    const novaLista = [...messages, novaPergunta];
    setMessages(novaLista);
    localStorage.setItem('rustchat-messages', JSON.stringify(novaLista));

    let contextText = '';
    try {
      const rawResults = localStorage.getItem('searchResults');
      const videos = rawResults ? JSON.parse(rawResults) : [];
      contextText = videos.map((v: any) => `• ${v.title}: ${v.description || 'Sem descrição.'}`).join('\n');
    } catch {
      contextText = '';
    }

    const response = await fetch('/api/search-comment', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: msg,
        context: contextText,
        voice_mode: voiceMode,
        current_page: window.location.pathname,
      }),
    });

    const data = await response.json();
    const respostaFinal = data.answer || 'Desculpe, não consegui responder agora. Deseja fazer outra pergunta?';

    const novaListaAtualizada = novaLista.map((m, i) =>
      i === novaLista.length - 1 ? { ...m, answer: respostaFinal } : m
    );
    setMessages(novaListaAtualizada);
    localStorage.setItem('rustchat-messages', JSON.stringify(novaListaAtualizada));

    if (voiceMode) speakText(respostaFinal);
    setMsg('');

    if (searchMode) {
      setTimeout(() => {
        setCollapsed(true);
        navigate(`/search?q=${encodeURIComponent(msg)}`);
      }, 1000);
    }
  };

  const startVoice = () => {
    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (!SpeechRecognition) return alert('Navegador sem suporte a voz');
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.lang = 'pt-BR';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setIsListening(true);
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (event: any) => setMsg(event.results[0][0].transcript);
    recognition.start();
  };

  return (
    <div
      id="rustchat-container"
      className="fixed z-50 pointer-events-none"
      style={{ left: dragPos.x, top: dragPos.y }}
      ref={dragRef}
    >
      <div className="pointer-events-auto bg-black/30 backdrop-blur-md p-4 rounded-xl shadow-2xl w-[360px] flex flex-col max-h-[70vh] border border-neutral-700">
        <div className="flex justify-between items-center mb-2 select-none cursor-move" data-drag="true">
          <h2 className="text-sm font-semibold text-white">RustChat Inteligente</h2>
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-6 h-6 flex items-center justify-center rounded hover:bg-neutral-600 text-white"
            title="Minimizar"
          >
            {collapsed ? '+' : '–'}
          </button>
        </div>

        {!collapsed && (
          <>
            <div
              className="flex-1 overflow-y-auto pr-2 space-y-4 text-sm"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(255,255,255,0.3) transparent',
              }}
            >
              {messages.map((m, idx) => (
                <React.Fragment key={idx}>
                  <div className="flex justify-end">
                    <div className="bg-orange-600 text-white p-3 rounded-lg max-w-[85%]">
                      <div className="text-xs font-bold text-right text-orange-200">Você</div>
                      <div className="whitespace-pre-wrap">{m.question}</div>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-neutral-800/80 text-gray-200 p-3 rounded-lg max-w-[85%]">
                      <div className="text-xs font-bold text-blue-400">Rustico.ai</div>
                      <div className="whitespace-pre-wrap">{m.answer}</div>
                    </div>
                  </div>
                </React.Fragment>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="flex items-center gap-2 mt-2">
              <input
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && perguntar()}
                className="flex-1 bg-neutral-700 border border-neutral-600 text-white p-2 rounded placeholder:text-gray-400"
                placeholder="Digite..."
                maxLength={300}
              />
              <button
                className={`w-10 h-10 flex items-center justify-center rounded-full ${
                  isListening ? 'bg-red-700' : 'bg-neutral-700'
                } hover:bg-neutral-600 text-white transition`}
                onClick={startVoice}
              >
                <Mic size={20} />
              </button>
              <button
                className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-500 hover:bg-blue-600 text-white transition"
                onClick={perguntar}
              >
                <Send size={20} />
              </button>
            </div>

            <div className="flex items-center justify-between mt-1 text-xs text-gray-400 px-1">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={voiceMode} onChange={() => setVoiceMode(!voiceMode)} className="accent-green-500" />
                Usar microfone
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={searchMode} onChange={() => setSearchMode(!searchMode)} className="accent-blue-500" />
                Modo busca inteligente
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RustChat;
